import React, { PureComponent, FunctionComponent } from 'react';
import {
  ScrollCtrlConsumer,
  IPosition,
  IBounds,
  IScrollSize,
  IScrollCtrlContext,
} from 'app/components/scroll/ScrollContext';
import throttle from 'lodash/throttle';

export interface IScrollEvent {
  readonly position: IPosition;
  readonly bounds: IBounds;
  readonly scrollSize: IScrollSize;
}

interface IProps {
  onScroll: (event: IScrollEvent) => void;
  throttle: number;
}

interface IDeps {
  scroller?: IScrollCtrlContext;
}

/**
 * Emits `onScroll` on mount and when the parent `ScrollProvider`
 * scrolls. Instead of the raw event, it passes position and
 * vieport's bounds to `onScroll`
 */
class OnScroll extends PureComponent<IProps & IDeps> {
  private readonly eventMap = {
    scroll: () => {
      if (this.onScrollThrottled) {
        this.onScrollThrottled();
      } else {
        this.onScroll();
      }
    },
  };

  private onScrollThrottled?: ReturnType<typeof throttle>;

  public componentDidMount() {
    this.updateScroller(undefined, this.props.scroller);
    this.updateThrottle(0, this.props.throttle);
    this.eventMap.scroll();
  }

  public componentDidUpdate({ scroller: oldScroller, throttle: oldThrottle }: IProps & IDeps) {
    this.updateScroller(oldScroller, this.props.scroller);
    this.updateThrottle(oldThrottle, this.props.throttle);
  }

  public componentWillUnmount() {
    this.updateThrottle(this.props.throttle, 0);
    this.updateScroller(this.props.scroller, undefined);
  }

  public render() {
    return this.props.children;
  }

  private updateScroller(oldScroller?: IScrollCtrlContext, newScroller?: IScrollCtrlContext) {
    if (newScroller !== oldScroller) {
      if (oldScroller) {
        oldScroller.unbindEvents(this.eventMap);
      }

      if (newScroller) {
        newScroller.bindEvents(this.eventMap);
      }
    }
  }

  private updateThrottle(oldThrottle: number, newThrottle: number) {
    if (oldThrottle !== newThrottle) {
      if (this.onScrollThrottled) {
        this.onScrollThrottled.cancel();
        this.onScrollThrottled = undefined;
      }

      if (newThrottle > 0) {
        this.onScrollThrottled = throttle(this.onScroll, newThrottle);
      }
    }
  }

  private readonly onScroll = () => {
    const { scroller } = this.props;

    if (scroller) {
      this.props.onScroll({
        bounds:     scroller.getBounds(),
        position:   scroller.getPosition(),
        scrollSize: scroller.getScrollSize(),
      });
    }
  };
}

const OnScrollInjectDependencies: FunctionComponent<IProps> = (props) =>
  <ScrollCtrlConsumer>{(scrollEmitter) =>
    <OnScroll scroller={scrollEmitter} {...props} />
  }</ScrollCtrlConsumer>;

export default OnScrollInjectDependencies;
